import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useModal } from './ModalContext';
import { useAuth } from './AuthContext';
import { useAccount } from './AccountContext';

const PlanUpgradeContext = createContext();

export const usePlanUpgrade = () => {
  const context = useContext(PlanUpgradeContext);
  if (!context) {
    throw new Error('usePlanUpgrade must be used within a PlanUpgradeProvider');
  }
  return context;
};

export const PlanUpgradeProvider = ({ children }) => {
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [upgradeApiState, setUpgradeApiState] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [wasUpgradeSubmitted, setWasUpgradeSubmitted] = useState(false); // Track if upgrade was actually submitted
  const { openErrorModal, openWarningModal, openSuccessModal, openConfirmModal } = useModal();
  const { logout } = useAuth();
  const { fetchSubscriptionData } = useAccount();
  const navigate = useNavigate();

  // Handle logout after successful upgrade
  const handleLogoutAfterSuccess = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Handle successful upgrade with subscription refresh
  const handleSuccessfulUpgrade = async () => {
    try {
      setIsPlansModalOpen(false);
      // Refresh subscription data
      const updatedPlan = await fetchSubscriptionData();
      const isStillExpired = typeof updatedPlan?.days_remaining === 'number' && updatedPlan.days_remaining <= 0;
      if (isStillExpired) {
        return;
      } else {
        openSuccessModal(
          'Upgrade Request Submitted',
          'Your upgrade request has been submitted for review. Our team will process it within 24 hours.',
          () => window.location.reload()
        );
      }
    } catch (error) {
      openErrorModal('Error', 'Failed to submit upgrade request. Please try again.');
    }
  };

  const checkUpgradeAllowed = async () => {
    try {
      const response = await api.get('subscription/new_request_allowed/');
      if (response.status === 200 && response.data.allowed === false) {
          openWarningModal(
            'Pending Upgrade Request',
            'A subscription request is already pending. Please wait until it is processed before submitting a new one.'
          );
        return false;
      }
      return true;
    } catch (error) {
      openErrorModal("Error", "Failed to check upgrade request.");
      return false;
    }
  };

  const fetchAvailablePlans = async () => {
    setLoading(true);
    try {
      const response = await api.get('subscription/plans/');
      if (response.status === 200) {
        setAvailablePlans(response.data);
        return true;
      }
      return false;
    } catch (error) {
      openErrorModal("Error", "Failed to fetch available plans.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgradeApiState('loading');
    setWasUpgradeSubmitted(true); // Mark that upgrade was submitted
    try {
      const response = await api.post('subscription/upgrade/', { plan_id: planId });
      if (response.status === 200 || response.status === 201) {
        setUpgradeApiState('success');
        handleSuccessfulUpgrade();
        return true;
      }
    } catch (error) {
      setUpgradeApiState('error');
      setWasUpgradeSubmitted(false); // Reset if failed
      openErrorModal("Error", "Failed to submit upgrade request.");
    } finally {
      setLoading(false);
    }
    return false;
  };

  const openPlansModal = async () => {
    const isAllowed = await checkUpgradeAllowed();
    if (isAllowed) {
      const plansLoaded = await fetchAvailablePlans();
      if (plansLoaded) {
        setIsPlansModalOpen(true);
      }
    }
  };

  const closePlansModal = () => {
    setIsPlansModalOpen(false);
    setAvailablePlans([]);
    // Don't reset wasUpgradeSubmitted here - let the consumer handle it
  };

  return (
    <PlanUpgradeContext.Provider value={{
      isPlansModalOpen,
      availablePlans,
      loading,
      openPlansModal,
      closePlansModal,
      handleUpgrade,
      upgradeApiState,
      wasUpgradeSubmitted,
      setWasUpgradeSubmitted
    }}>
      {children}
    </PlanUpgradeContext.Provider>
  );
};

export default PlanUpgradeProvider;